import type { PosServiceLineInput, PosServiceLineKind } from '@tcr-erp/shared';
import { resolveDisplayVatRate, type ServiceOption } from '../components/ServiceFeeRow';

const DEFAULT_LINE_NAME: Record<PosServiceLineKind, string> = {
  surcharge: 'Phụ thu',
  shipping: 'Phí vận chuyển',
};

/**
 * Dựng MỘT dòng dịch vụ cấp đơn (phụ thu hoặc phí ship tự vận chuyển) cho FE
 * gọi `computePosMoney` khi hiển thị — mirror `resolve-service-line.ts` phía
 * BE (`pos-order.service.ts` dùng chính hàm đó khi ghi đơn thật).
 *
 * CHỈ điền chính xác những field ảnh hưởng tới CÔNG THỨC tính tiền — `amount`,
 * `vatRate`, `taxable`, `kind`. `serviceCode`/`unitName`/`vatRateId`/`vatSource`
 * không được `compute.ts` đọc ở bất kỳ phép tính nào (thuần snapshot cho hoá
 * đơn, BE tự tra lại khi ghi đơn thật) — FE không có đủ dữ liệu tra `unit_name`
 * (chỉ có `unit_id`) nên không cố tái tạo, dùng giá trị mặc định vô hại.
 *
 * Trả `null` khi dòng không có tiền — Y HỆT `resolveServiceLine` phía BE: 0đ
 * không sinh dòng, không vào nhóm thuế suất. Thiếu nhánh này thì FE cộng thừa
 * một dòng "0đ" vào `serviceLines`, lệch số dòng so với BE (vô hại về số nhưng
 * lệch cấu trúc `taxBreakdowns` khi dòng 0đ vẫn được nhóm).
 */
export function buildPosServiceLine(
  kind: PosServiceLineKind,
  serviceId: string | null,
  amount: number,
  taxable: boolean,
  options: ServiceOption[],
  defaultVatRate: number,
): PosServiceLineInput | null {
  const roundedAmount = Math.max(0, Math.round(amount || 0));
  if (roundedAmount === 0) return null;

  const option = options.find((o) => o.id === serviceId) ?? null;
  const { rate } = resolveDisplayVatRate(serviceId, options, defaultVatRate);

  return {
    kind,
    serviceId,
    serviceCode: null,
    serviceName: option?.name ?? DEFAULT_LINE_NAME[kind],
    unitName: 'Lần',
    amount: roundedAmount,
    vatRate: rate,
    vatRateId: null,
    vatSource: option && option.vatRate !== null ? 'service' : 'default_setting',
    taxable,
  };
}
